<template>
  <BackofficeLayout>
    <div class="space-y-6">
      <!-- Header -->
      <div class="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 bg-white p-6 rounded-2xl border border-slate-200 shadow-2xs">
        <div>
          <h1 class="text-xl font-bold text-slate-900">Manajemen Master SKU</h1>
          <p class="text-xs text-slate-500 mt-1">Kelola varian produk, harga nominal, HPP komersial, dan harga jual fallback PPOB.</p>
        </div>
        <button
          @click="openCreateModal"
          type="button"
          class="px-4 py-2.5 bg-indigo-600 hover:bg-indigo-700 text-white font-bold text-xs rounded-xl shadow-xs transition flex items-center justify-center gap-2"
        >
          <Plus class="w-4 h-4" />
          <span>Tambah Master SKU</span>
        </button>
      </div>

      <!-- Flash Status -->
      <div v-if="$page.props.flash.status" class="p-4 bg-emerald-50 border border-emerald-200 text-emerald-800 text-xs font-semibold rounded-xl">
        {{ $page.props.flash.status }}
      </div>

      <!-- Filters & Table Section -->
      <div class="bg-white rounded-2xl border border-slate-200 shadow-2xs overflow-hidden">
        <div class="p-4 border-b border-slate-200 bg-slate-50 flex flex-wrap items-center justify-between gap-4">
          <div class="flex flex-wrap items-center gap-3">
            <select v-model="selectedCategory" @change="applyFilters" class="px-3.5 py-2 bg-white border border-slate-300 text-slate-900 rounded-xl text-xs focus:outline-none focus:ring-2 focus:ring-indigo-500">
              <option :value="null">Semua Kategori</option>
              <option v-for="cat in categories" :key="cat.id" :value="cat.id">{{ cat.name }}</option>
            </select>
            <select v-model="selectedProduct" @change="applyFilters" class="px-3.5 py-2 bg-white border border-slate-300 text-slate-900 rounded-xl text-xs focus:outline-none focus:ring-2 focus:ring-indigo-500">
              <option :value="null">Semua Produk</option>
              <option v-for="p in products" :key="p.id" :value="p.id">{{ p.name }}</option>
            </select>
            <input
              v-model="search"
              type="text"
              placeholder="Cari SKU (nama, kode)..."
              class="px-3.5 py-2 bg-white border border-slate-300 text-slate-900 rounded-xl text-xs w-56 focus:outline-none focus:ring-2 focus:ring-indigo-500"
              @input="applyFilters"
            />
          </div>
          <span class="text-xs font-semibold text-slate-500">Total: {{ skus.length }} SKU</span>
        </div>

        <div class="overflow-x-auto">
          <table class="w-full text-left border-collapse text-xs">
            <thead>
              <tr class="border-b border-slate-200 bg-slate-100/70 text-slate-700 font-bold uppercase tracking-wider">
                <th class="py-3 px-4">Kode SKU</th>
                <th class="py-3 px-4">Nama SKU</th>
                <th class="py-3 px-4">Produk Induk</th>
                <th class="py-3 px-4 text-right">Nominal</th>
                <th class="py-3 px-4 text-right">HPP Komersial</th>
                <th class="py-3 px-4 text-right">Harga Jual Fallback</th>
                <th class="py-3 px-4">Status</th>
                <th class="py-3 px-4 text-right">Aksi</th>
              </tr>
            </thead>
            <tbody class="divide-y divide-slate-100 text-slate-800">
              <tr v-for="sku in skus" :key="sku.id" class="hover:bg-slate-50/80 transition">
                <td class="py-3 px-4 font-mono font-bold text-indigo-600">{{ sku.code }}</td>
                <td class="py-3 px-4 font-bold text-slate-900">{{ sku.name }}</td>
                <td class="py-3 px-4">
                  <p class="font-semibold text-slate-800">{{ sku.product?.name || '-' }}</p>
                  <span class="text-[10px] text-slate-500 font-medium">{{ sku.product?.category?.name || '-' }}</span>
                </td>
                <td class="py-3 px-4 text-right font-mono font-bold text-slate-700">Rp {{ sku.nominal_minor.toLocaleString('id-ID') }}</td>
                <td class="py-3 px-4 text-right">
                  <div class="flex items-center justify-end gap-1.5">
                    <span class="font-mono font-bold text-slate-900">Rp {{ sku.commercial_hpp_minor.toLocaleString('id-ID') }}</span>
                    <button @click="openHppHistoryModal(sku)" type="button" title="Riwayat HPP" class="p-1 hover:bg-slate-200 rounded text-slate-500 hover:text-indigo-600 transition">
                      <History class="w-3.5 h-3.5" />
                    </button>
                  </div>
                </td>
                <td class="py-3 px-4 text-right font-mono font-bold text-emerald-700">Rp {{ sku.fallback_selling_price_minor.toLocaleString('id-ID') }}</td>
                <td class="py-3 px-4">
                  <span
                    :class="sku.is_active ? 'bg-emerald-100 text-emerald-800 border-emerald-200' : 'bg-slate-100 text-slate-600 border-slate-200'"
                    class="px-2.5 py-1 rounded-full border text-[10px] font-bold"
                  >
                    {{ sku.is_active ? 'Aktif' : 'Nonaktif' }}
                  </span>
                </td>
                <td class="py-3 px-4 text-right space-x-2">
                  <button
                    @click="toggleActive(sku)"
                    type="button"
                    class="px-2 py-1 rounded-lg border text-[11px] font-semibold transition"
                    :class="sku.is_active ? 'border-amber-200 bg-amber-50 text-amber-700 hover:bg-amber-100' : 'border-emerald-200 bg-emerald-50 text-emerald-700 hover:bg-emerald-100'"
                  >
                    {{ sku.is_active ? 'Nonaktifkan' : 'Aktifkan' }}
                  </button>
                  <button @click="openEditModal(sku)" type="button" class="px-2 py-1 rounded-lg border border-slate-200 bg-slate-100 text-slate-700 hover:bg-slate-200 text-[11px] font-semibold transition">
                    Edit
                  </button>
                  <button @click="deleteSku(sku)" type="button" class="px-2 py-1 rounded-lg border border-rose-200 bg-rose-50 text-rose-700 hover:bg-rose-100 text-[11px] font-semibold transition">
                    Hapus
                  </button>
                </td>
              </tr>
              <tr v-if="skus.length === 0">
                <td colspan="8" class="py-8 text-center text-slate-500 text-xs">
                  Tidak ada data Master SKU.
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>

      <!-- Modal Create / Edit SKU -->
      <div v-if="showModal" class="fixed inset-0 bg-slate-900/60 backdrop-blur-xs z-50 flex items-center justify-center p-4">
        <div class="bg-white rounded-2xl max-w-lg w-full p-6 space-y-4 shadow-xl border border-slate-200 max-h-[90vh] overflow-y-auto">
          <h3 class="text-base font-bold text-slate-900">{{ isEditing ? 'Edit Master SKU' : 'Tambah Master SKU' }}</h3>

          <form @submit.prevent="submitForm" class="space-y-4">
            <div>
              <label class="block text-xs font-bold text-slate-700 mb-1">Produk Induk</label>
              <select v-model="form.master_product_id" :disabled="isEditing" required class="w-full px-3.5 py-2.5 rounded-xl border border-slate-300 text-xs focus:outline-none focus:ring-2 focus:ring-indigo-500">
                <option value="" disabled>Pilih Master Produk</option>
                <option v-for="p in products" :key="p.id" :value="p.id">{{ p.name }} ({{ p.code }})</option>
              </select>
            </div>

            <div class="grid grid-cols-2 gap-3">
              <div>
                <label class="block text-xs font-bold text-slate-700 mb-1">Kode SKU</label>
                <input v-model="form.code" type="text" placeholder="S5" required class="w-full px-3.5 py-2.5 rounded-xl border border-slate-300 text-xs focus:outline-none focus:ring-2 focus:ring-indigo-500 font-mono" />
              </div>
              <div>
                <label class="block text-xs font-bold text-slate-700 mb-1">Nama SKU</label>
                <input v-model="form.name" type="text" placeholder="Telkomsel 5.000" required class="w-full px-3.5 py-2.5 rounded-xl border border-slate-300 text-xs focus:outline-none focus:ring-2 focus:ring-indigo-500" />
              </div>
            </div>

            <div class="grid grid-cols-3 gap-3">
              <div>
                <label class="block text-xs font-bold text-slate-700 mb-1">Nominal (Rupiah)</label>
                <input v-model.number="form.nominal_minor" type="number" min="0" required class="w-full px-3 py-2 rounded-xl border border-slate-300 text-xs font-mono" />
              </div>
              <div>
                <label class="block text-xs font-bold text-slate-700 mb-1">HPP Komersial</label>
                <input v-model.number="form.commercial_hpp_minor" type="number" min="0" required class="w-full px-3 py-2 rounded-xl border border-slate-300 text-xs font-mono" />
              </div>
              <div>
                <label class="block text-xs font-bold text-slate-700 mb-1">Harga Jual Fallback</label>
                <input v-model.number="form.fallback_selling_price_minor" type="number" min="0" required class="w-full px-3 py-2 rounded-xl border border-slate-300 text-xs font-mono" />
              </div>
            </div>

            <div v-if="form.fallback_selling_price_minor < form.commercial_hpp_minor" class="p-3 bg-amber-50 border border-amber-200 rounded-xl space-y-2">
              <p class="text-[11px] text-amber-800 font-bold">Peringatan Margin</p>
              <p class="text-[11px] text-amber-700">Harga jual fallback lebih kecil dari HPP komersial.</p>
              <div class="flex items-center gap-2">
                <input v-model="form.allow_lower_selling_price" type="checkbox" id="allow_lower_price" class="rounded text-amber-600 focus:ring-amber-500" />
                <label for="allow_lower_price" class="text-xs font-semibold text-amber-900">Saya menyetujui harga jual di bawah HPP</label>
              </div>
            </div>

            <div v-if="isEditing" class="p-3 bg-slate-50 border border-slate-200 rounded-xl">
              <label class="block text-xs font-bold text-slate-700 mb-1">Alasan Perubahan (Catatan HPP)</label>
              <input v-model="form.hpp_change_reason" type="text" placeholder="misal: Penyesuaian HPP Komersial Juli" class="w-full px-3 py-2 rounded-xl border border-slate-300 text-xs" />
            </div>

            <div class="grid grid-cols-2 gap-3">
              <div>
                <label class="block text-xs font-bold text-slate-700 mb-1">Urutan (Sort Order)</label>
                <input v-model.number="form.sort_order" type="number" min="0" class="w-full px-3.5 py-2.5 rounded-xl border border-slate-300 text-xs" />
              </div>
              <div class="flex items-center gap-2 pt-6">
                <input v-model="form.is_active" type="checkbox" id="sku_is_active" class="rounded text-indigo-600 focus:ring-indigo-500" />
                <label for="sku_is_active" class="text-xs font-semibold text-slate-700">SKU Aktif</label>
              </div>
            </div>

            <div class="flex justify-end gap-2 pt-2">
              <button type="button" @click="showModal = false" class="px-4 py-2 text-xs font-semibold text-slate-600 hover:bg-slate-100 rounded-xl transition">Batal</button>
              <button type="submit" :disabled="form.processing" class="px-4 py-2 text-xs font-bold bg-indigo-600 text-white rounded-xl hover:bg-indigo-700 transition">
                Simpan
              </button>
            </div>
          </form>
        </div>
      </div>

      <!-- Modal HPP History -->
      <div v-if="showHppModal" class="fixed inset-0 bg-slate-900/60 backdrop-blur-xs z-50 flex items-center justify-center p-4">
        <div class="bg-white rounded-2xl max-w-md w-full p-6 space-y-4 shadow-xl border border-slate-200">
          <div class="flex items-center justify-between">
            <h3 class="text-base font-bold text-slate-900">Riwayat HPP Komersial</h3>
            <button @click="showHppModal = false" class="text-slate-400 hover:text-slate-600 text-xs font-bold">Tutup</button>
          </div>

          <div v-if="selectedSkuHistory" class="space-y-3">
            <div class="p-3 bg-slate-50 border border-slate-200 rounded-xl text-xs space-y-1">
              <p class="font-bold text-slate-800">{{ selectedSkuHistory.sku.name }} ({{ selectedSkuHistory.sku.code }})</p>
              <p class="text-slate-500">HPP Saat Ini: <strong class="text-indigo-600">Rp {{ selectedSkuHistory.sku.current_hpp_minor.toLocaleString('id-ID') }}</strong></p>
            </div>

            <div class="max-h-60 overflow-y-auto space-y-2">
              <div v-for="item in selectedSkuHistory.history" :key="item.id" class="p-3 border border-slate-200 rounded-xl text-xs space-y-1">
                <div class="flex justify-between items-center font-bold">
                  <span class="text-slate-900">Rp {{ item.commercial_hpp_minor.toLocaleString('id-ID') }}</span>
                  <span v-if="!item.effective_until" class="px-2 py-0.5 bg-emerald-100 text-emerald-800 rounded-full text-[10px]">Aktif Saat Ini</span>
                  <span v-else class="text-slate-400 text-[10px]">Kedaluwarsa</span>
                </div>
                <p class="text-slate-500 text-[11px]">Berlaku: {{ formatDate(item.effective_from) }} {{ item.effective_until ? '- ' + formatDate(item.effective_until) : '' }}</p>
                <p v-if="item.reason" class="text-slate-600 italic text-[11px]">Catatan: {{ item.reason }}</p>
                <p v-if="item.created_by" class="text-slate-400 text-[10px]">Oleh: {{ item.created_by.display_name }}</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </BackofficeLayout>
</template>

<script setup>
import { ref } from 'vue';
import { useForm, router } from '@inertiajs/vue3';
import BackofficeLayout from '@/Layouts/BackofficeLayout.vue';
import { Plus, History } from '@lucide/vue';
import axios from 'axios';

const props = defineProps({
  skus: Array,
  products: Array,
  categories: Array,
  filters: Object,
});

const search = ref(props.filters?.search || '');
const selectedProduct = ref(props.filters?.master_product_id ? Number(props.filters.master_product_id) : null);
const selectedCategory = ref(props.filters?.product_category_id ? Number(props.filters.product_category_id) : null);
const showModal = ref(false);
const isEditing = ref(false);
const editingSku = ref(null);

const showHppModal = ref(false);
const selectedSkuHistory = ref(null);

const form = useForm({
  master_product_id: '',
  code: '',
  name: '',
  nominal_minor: 0,
  commercial_hpp_minor: 0,
  fallback_selling_price_minor: 0,
  allow_lower_selling_price: false,
  hpp_change_reason: '',
  description: '',
  sort_order: 0,
  is_active: true,
});

const openCreateModal = () => {
  isEditing.value = false;
  editingSku.value = null;
  form.reset();
  form.clearErrors();
  showModal.value = true;
};

const openEditModal = (sku) => {
  isEditing.value = true;
  editingSku.value = sku;
  form.master_product_id = sku.master_product_id;
  form.code = sku.code;
  form.name = sku.name;
  form.nominal_minor = sku.nominal_minor;
  form.commercial_hpp_minor = sku.commercial_hpp_minor;
  form.fallback_selling_price_minor = sku.fallback_selling_price_minor;
  form.allow_lower_selling_price = sku.fallback_selling_price_minor < sku.commercial_hpp_minor;
  form.hpp_change_reason = '';
  form.description = sku.description;
  form.sort_order = sku.sort_order;
  form.is_active = sku.is_active;

  form.clearErrors();
  showModal.value = true;
};

const openHppHistoryModal = async (sku) => {
  try {
    const res = await axios.get(route('admin.catalogue.skus.hpp-history', sku.id));
    selectedSkuHistory.value = res.data;
    showHppModal.value = true;
  } catch (e) {
    alert('Gagal mengambil riwayat HPP.');
  }
};

const formatDate = (str) => {
  if (!str) return '-';
  return new Date(str).toLocaleString('id-ID', { dateStyle: 'medium', timeStyle: 'short' });
};

const submitForm = () => {
  if (isEditing.value && editingSku.value) {
    form.put(route('admin.catalogue.skus.update', editingSku.value.id), {
      onSuccess: () => { showModal.value = false; },
    });
  } else {
    form.post(route('admin.catalogue.skus.store'), {
      onSuccess: () => { showModal.value = false; },
    });
  }
};

const toggleActive = (sku) => {
  router.patch(route('admin.catalogue.skus.toggle-active', sku.id), {
    is_active: !sku.is_active,
  });
};

const deleteSku = (sku) => {
  if (confirm(`Apakah Anda yakin ingin menghapus master SKU '${sku.name}'?`)) {
    router.delete(route('admin.catalogue.skus.destroy', sku.id));
  }
};

const applyFilters = () => {
  router.get(route('admin.catalogue.skus.index'), {
    product_category_id: selectedCategory.value,
    master_product_id: selectedProduct.value,
    search: search.value,
  }, { preserveState: true, replace: true });
};
</script>
